import { apiErrorFrom } from '@/utils/apiError';
import type { PaginatedList, Pagination } from '@/types/activity';

/**
 * Desembrulha o envelope `{ success, data }` do marketingbackgo. Resposta com falha vira um
 * `ApiError` (mensagem pt-BR do backend + erros por campo), pronto para o ApiErrorModal.
 */
export const readData = async <T>(res: Response, fallback: string): Promise<T> => {
  if (!res.ok) throw await apiErrorFrom(res, fallback);
  const json = await res.json().catch(() => ({}));
  return (json?.data ?? null) as T;
};

/** Lista simples: garante array mesmo quando o backend devolve `null`. */
export const readList = async <T>(res: Response, fallback: string): Promise<T[]> => {
  const data = await readData<T[] | null>(res, fallback);
  return Array.isArray(data) ? data : [];
};

/** Lista paginada: `{ success, data: [], pagination: {...} }`. */
export const readPaginated = async <T>(res: Response, fallback: string): Promise<PaginatedList<T>> => {
  if (!res.ok) throw await apiErrorFrom(res, fallback);
  const json = await res.json().catch(() => ({}));
  const data: T[] = Array.isArray(json?.data) ? json.data : [];
  const pg: Partial<Pagination> = json?.pagination ?? {};
  return {
    data,
    pagination: {
      page: pg.page ?? 1,
      page_size: pg.page_size ?? data.length,
      total_items: pg.total_items ?? data.length,
      total_pages: pg.total_pages ?? 1,
    },
  };
};

/** Resposta sem corpo útil (DELETE, read-all): só valida o status. */
export const ensureOk = async (res: Response, fallback: string): Promise<void> => {
  if (!res.ok) throw await apiErrorFrom(res, fallback);
};

/** Monta a query string ignorando vazios/indefinidos/false. */
export const toQuery = (params: Record<string, string | number | boolean | undefined | null>): string => {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    qs.set(key, String(value));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
};
